import React, { FC, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import TwCheckbox from '../TwCheckboxes/TwCheckboxes';
import DetailFile from '../TwLoadDetail/DetailFile/DetailFile';
import StringUtils from '../../utils/stringUtils';
import TwPhotoThumbnail from '../TwPhotoThumbnail/TwPhotoThumbnail';
import { TwButton, TwButtonType } from '../TwButtons/TwButtons';
import { ReactComponent as UploadIcon } from '../../assets/icons/upload.svg';
import { OrderAttachment } from '../../redux/api/orders/orders.model';
interface TwSelectableDocsPhotosProps {
  documents: OrderAttachment[];
  photos: OrderAttachment[];
  showUploadButton?: boolean;
  disableEncryptedDocuments?: boolean;
  onSelectDocuments: (documents: string[]) => void;
  onSelectPhotos: (photos: string[]) => void;
  onDocumentClick?: (document: OrderAttachment) => void;
  onPhotoClick?: (photo: OrderAttachment) => void;
  onUploadButtonClick?: () => void;
}

const TwSelectableDocsPhotos: FC<TwSelectableDocsPhotosProps> = ({
  documents,
  photos,
  showUploadButton = false,
  disableEncryptedDocuments = false,
  onSelectDocuments,
  onSelectPhotos,
  onDocumentClick,
  onPhotoClick,
  onUploadButtonClick
}) => {
  const [selectedDocs, setSelectedDocs] = useState<string[]>([]);
  const [selectedPhotos, setSelectedPhotos] = useState<string[]>([]);

  const handleToggleAllDocuments = (): void => {
    const newSelectedDocs =
      selectedDocs.length >= 1
        ? []
        : documents
            .filter((doc) => !disableEncryptedDocuments || !doc.is_encrypted)
            .map((doc) => doc.id);
    setSelectedDocs(newSelectedDocs);
    onSelectDocuments(newSelectedDocs); // update to parent
  };

  const handleToggleAllPhotos = (): void => {
    const newSelectedPhotos = selectedPhotos.length >= 1 ? [] : photos.map((ph) => ph.id);
    setSelectedPhotos(newSelectedPhotos);
    onSelectPhotos(newSelectedPhotos); // update to parent
  };

  const handleInputChangeDocs = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const valueSelected = selectedDocs?.indexOf(e.currentTarget.value);

    let selectedDocsUpdated: string[] = [];
    if (valueSelected === -1 || selectedDocs?.length === 0) {
      selectedDocsUpdated = [...selectedDocs, e.currentTarget.value];
    } else {
      selectedDocsUpdated = selectedDocs.filter((item) => item !== e.currentTarget.value);
    }

    setSelectedDocs(selectedDocsUpdated);
    onSelectDocuments(selectedDocsUpdated); // update to parent
  };

  const handleInputChangePhotos = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const valueSelected = selectedPhotos?.indexOf(e.currentTarget.value);
    let selectedPhotosUpdated: string[] = [];
    if (valueSelected === -1 || selectedPhotos?.length === 0) {
      selectedPhotosUpdated = [...selectedPhotos, e.currentTarget.value];
    } else {
      selectedPhotosUpdated = selectedPhotos.filter((item) => item !== e.currentTarget.value);
    }

    setSelectedPhotos(selectedPhotosUpdated);
    onSelectPhotos(selectedPhotosUpdated); // update to parent
  };

  const handlePhotoClick = (photo: OrderAttachment): void => {
    onPhotoClick && onPhotoClick(photo);
  };

  const handleDocumentClick = (doc: OrderAttachment): void => {
    onDocumentClick && onDocumentClick(doc);
  };

  const handleUploadButtonClick = (): void => {
    onUploadButtonClick && onUploadButtonClick();
  };

  /**
   * Effect to select all documents when documents are loaded
   */
  useEffect(() => {
    if (documents.length === 0) return;
    const docsIds =
      documents
        .filter((doc) => !disableEncryptedDocuments || !doc.is_encrypted)
        .map((doc) => doc.id) || [];

    setSelectedDocs(docsIds);
    onSelectDocuments(docsIds);
  }, [documents]);

  /**
   * Effect to select all photos when photos are loaded
   */
  useEffect(() => {
    if (photos.length === 0) return;
    const photosIds = photos.map((ph) => ph.id) || [];
    setSelectedPhotos(photosIds);
    onSelectPhotos(photosIds);
  }, [photos]);

  useEffect(() => {
    if (disableEncryptedDocuments && documents.length > 0 && selectedDocs.length > 0) {
      const docsIds =
        documents
          .filter((doc) => !doc.is_encrypted && selectedDocs.includes(doc.id))
          .map((doc) => doc.id) || [];
      setSelectedDocs(docsIds);
      onSelectDocuments(docsIds);
    }
  }, [disableEncryptedDocuments]);

  return (
    <Box>
      {documents.length > 0 && (
        <TwCheckbox
          name="selectAll-documents"
          label={`${selectedDocs.length} document(s) selected`}
          onInputChange={handleToggleAllDocuments}
          checked={selectedDocs.length === documents.length}
          indeterminate={selectedDocs.length > 0 && selectedDocs.length < documents.length}
          sxCheckbox={{ marginLeft: '9px' }}
        />
      )}

      {documents.map((document) => (
        <DetailFile
          key={document.id}
          isSelectable={true}
          onInputChange={handleInputChangeDocs}
          checked={selectedDocs?.find((doc) => doc === document.id) ? true : false}
          fileId={document.id}
          fileName={document.attachment.name}
          type={StringUtils.getLoadDocumentTypeLabel(document.type)}
          created_at={document.created_at ? new Date(document.created_at) : new Date()}
          onClick={(): void => handleDocumentClick(document)}
          isEncrypted={document.is_encrypted}
          disableEncryptedDocuments={disableEncryptedDocuments}
          attachmentId={document.id}
        />
      ))}

      {showUploadButton && (
        <Box>
          <TwButton
            key="1"
            type={TwButtonType.secondary}
            label="Upload"
            startIcon={<UploadIcon />}
            action={handleUploadButtonClick}
            className="create-invoice__upload"
          />
        </Box>
      )}

      {photos.length > 0 && (
        <TwCheckbox
          name="selectAll-photos"
          label={`${selectedPhotos.length} image(s) selected`}
          onInputChange={handleToggleAllPhotos}
          checked={selectedPhotos.length === photos.length}
          indeterminate={selectedPhotos.length > 0 && selectedPhotos.length < photos.length}
          sxCheckbox={{ marginLeft: '9px' }}
        />
      )}

      <div className="save-pdf__photos-thumbs">
        {photos.map((photo) => (
          <TwPhotoThumbnail
            key={photo.id}
            name={photo.id}
            photoId={photo.id}
            photoKey={photo.attachment.key}
            isSelectable={true}
            checked={selectedPhotos?.find((ph) => ph === photo.id) ? true : false}
            onInputChange={handleInputChangePhotos}
            src={photo.src}
            onClick={(): void => handlePhotoClick(photo)}
          />
        ))}
      </div>
    </Box>
  );
};

export default TwSelectableDocsPhotos;
